import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiLogs, ApiLogEntry, ApiLogsPage } from '../../api/services/apiLogsService';
import ManagementAuthFailures from './AuthFailures';
import { ActionRow, Card, CloseButton, FilterChip, ICONS, LineIcon, LinkButton, SectionLabel, StatusPill } from '../../components/ui/dg';

/**
 * Journaux API : la table `api_logs`, où l'API consigne chacun de ses appels
 * (route, statut, corps et réponse expurgés par redact.js côté serveur).
 *
 * Pastilles : toutes, erreurs serveur (5xx), refusées (4xx), lentes (500 ms
 * et plus), avec le compte des dernières 24 h. Une ligne ouvre son détail à
 * droite. Une erreur survenue dans l'app avant tout appel réseau (connexion
 * Google refusée par le téléphone, par exemple) n'y figure jamais : l'app la
 * signale à part, dans « Échecs de connexion » (AuthFailures.tsx).
 */

const PAGE_SIZE = 50;
const SLOW_MS = 500;
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

type Chip = 'all' | 'server' | 'refused' | 'slow';

const COLS = 'grid-cols-[96px_84px_minmax(0,2.2fr)_70px_90px_minmax(0,1.1fr)_16px]';

const METHOD_TONE: Record<string, string> = {
  GET: 'bg-[#EFEEFD] text-[#4F46E5]',
  POST: 'bg-[#ECFDF5] text-[#047857]',
  PUT: 'bg-[#FFF4DB] text-[#B45309]',
  PATCH: 'bg-[#FFF4DB] text-[#B45309]',
  DELETE: 'bg-[#FDECEF] text-[#B91C1C]'
};

const MethodPill = ({ method }: { method: string }) => (
  <span className={`inline-flex rounded-lg px-2 py-[3px] text-[10.5px] font-bold leading-[14px] ${METHOD_TONE[method] || 'bg-[#F1F2F8] text-[#475569]'}`}>{method}</span>
);

const CodePill = ({ status }: { status: number | null }) => (
  <StatusPill tone={status == null ? 'grey' : status >= 500 ? 'red' : status >= 400 ? 'amber' : 'green'}>{status ?? '—'}</StatusPill>
);

const ms = (value: number | null) => (value == null ? '—' : `${value.toLocaleString('fr-FR')} ms`);
const time = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dayLabel = (iso: string) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};
const shortName = (name?: string | null) => {
  if (!name) return null;
  const [first, ...rest] = name.trim().split(/\s+/);
  return rest.length ? `${first} ${rest[rest.length - 1][0]}.` : first;
};

const prettyJson = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const ManagementApiLogs = () => {
  const [view, setView] = useState<'calls' | 'auth'>('calls');
  if (view === 'auth') {
    return (
      <main className="flex-1 space-y-4 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
        <button type="button" onClick={() => setView('calls')} className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#4F46E5]">
          <LineIcon d={ICONS.chevron} size={14} color="#4F46E5" width={2.4} className="rotate-180" />
          Retour aux requêtes
        </button>
        <ManagementAuthFailures />
      </main>
    );
  }
  return <ApiCalls onAuthFailures={() => setView('auth')} />;
};

const ApiCalls = ({ onAuthFailures }: { onAuthFailures: () => void }) => {
  const { token } = useAuth();
  const [logs, setLogs] = useState<ApiLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<ApiLogsPage['last_24h']>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [chip, setChip] = useState<Chip>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [method, setMethod] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState<{ id: string; name: string } | null>(null);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<ApiLogEntry | null>(null);

  // La recherche porte sur les corps JSON de toute la table (ILIKE) : on
  // attend que la frappe se calme avant d'interroger.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setOffset(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    getApiLogs(token, {
      minStatus: chip === 'server' ? 500 : chip === 'refused' ? 400 : undefined,
      maxStatus: chip === 'refused' ? 499 : undefined,
      minMs: chip === 'slow' ? SLOW_MS : undefined,
      method: method || undefined,
      search: search || undefined,
      userId: userId?.id,
      limit: PAGE_SIZE,
      offset
    })
      .then((res) => {
        setLogs((cur) => (offset === 0 ? res.logs : [...cur, ...res.logs]));
        setTotal(res.pagination.total);
        if (res.last_24h) setCounts(res.last_24h);
      })
      .catch((err: any) => setError(err.message || 'Erreur lors du chargement des journaux'))
      .finally(() => setLoading(false));
  }, [token, chip, method, search, userId, offset]);

  useEffect(() => { load(); }, [load]);

  // Un filtre qui change repart de la première page.
  const reset = () => { setOffset(0); setSelected(null); };
  const pickChip = (value: Chip) => { setChip(value); reset(); };
  const activeFilters = (method ? 1 : 0) + (search ? 1 : 0) + (userId ? 1 : 0);

  return (
    <main className="flex min-h-0 flex-1 gap-6 overflow-hidden px-4 pb-6 pt-1 lg:px-7">
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto [&>*]:shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={chip === 'all'} onClick={() => pickChip('all')}>Toutes</FilterChip>
          <FilterChip active={chip === 'server'} count={counts?.server_errors || null} countTone="red" onClick={() => pickChip('server')}>Erreurs serveur</FilterChip>
          <FilterChip active={chip === 'refused'} count={counts?.refused || null} countTone="amber" onClick={() => pickChip('refused')}>Refusées</FilterChip>
          <FilterChip active={chip === 'slow'} count={counts?.slow || null} countTone="amber" onClick={() => pickChip('slow')}>Lentes</FilterChip>
          <span className="flex-1" />
          <FilterChip active={filtersOpen} count={activeFilters || null} onClick={() => setFiltersOpen((v) => !v)}
            icon={<LineIcon d={ICONS.filter} size={14} color={filtersOpen ? '#fff' : '#4B5563'} />}>Filtrer</FilterChip>
        </div>

        {filtersOpen && (
          <Card className="mt-3 flex flex-wrap items-end gap-4 px-5 py-4">
            <label className="block">
              <SectionLabel>Méthode</SectionLabel>
              <select value={method} onChange={(e) => { setMethod(e.target.value); reset(); }}
                className="mt-1.5 block h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:border-[#C7C4F6] focus:outline-none">
                <option value="">Toutes</option>
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="block min-w-[240px] flex-1">
              <SectionLabel>Recherche</SectionLabel>
              <input type="search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Route, message, e-mail…"
                className="mt-1.5 block h-10 w-full rounded-xl border border-[#E5E7EB] px-3 text-[13.5px] focus:border-[#C7C4F6] focus:outline-none" />
            </label>
            {userId && (
              <button type="button" onClick={() => { setUserId(null); reset(); }}
                className="inline-flex h-10 items-center gap-2 rounded-full bg-[#EFEEFD] px-4 text-[13px] font-bold text-[#4F46E5]">
                {userId.name}
                <LineIcon d={ICONS.close} size={12} color="#4F46E5" width={2.4} />
              </button>
            )}
          </Card>
        )}

        <Card className="mt-4 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className={`grid ${COLS} items-center gap-3 border-b border-[#EEF0F4] px-7 pb-3 pt-4`}>
                {['Heure', 'Méthode', 'Route', 'Code', 'Durée', 'Personne'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
                <span />
              </div>
              {error && <p className="px-7 py-4 text-[13px] text-[#B91C1C]">{error}</p>}
              {loading && logs.length === 0 ? (
                <div className="space-y-2 p-4">{Array.from({ length: 7 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-xl bg-[#EEF0F5]" />)}</div>
              ) : logs.length === 0 ? (
                <p className="px-7 py-14 text-center text-[13px] text-[#6B7280]">Aucune requête pour ces critères.</p>
              ) : logs.map((log) => {
                const active = selected?.id === log.id;
                const failed = (log.status ?? 0) >= 500;
                const slow = (log.execution_time_ms ?? 0) >= SLOW_MS;
                return (
                  <button key={log.id} type="button" onClick={() => setSelected(active ? null : log)}
                    className={`grid w-full ${COLS} items-center gap-3 border-b border-[#EEF0F4] px-7 py-[10px] text-left transition-colors last:border-b-0 ${
                      active ? (failed ? 'bg-[#FFF5F6] shadow-[inset_4px_0_0_#DC2626]' : 'bg-[#F5F4FE] shadow-[inset_4px_0_0_#4F46E5]') : 'hover:bg-[#FAFAFD]'}`}>
                    <span className="text-[13.5px] tabular-nums text-[#6B7280]">
                      {time(log.timestamp)}
                      {dayLabel(log.timestamp) && <span className="block text-[11px] text-[#9CA3AF]">{dayLabel(log.timestamp)}</span>}
                    </span>
                    <span><MethodPill method={log.method} /></span>
                    <span className="truncate font-mono text-[13.5px] text-[#1E293B]" title={log.route}>{log.route}</span>
                    <span><CodePill status={log.status} /></span>
                    <span className={`text-[13.5px] tabular-nums ${slow ? 'text-[#B91C1C]' : 'text-[#4B5563]'}`}>{ms(log.execution_time_ms)}</span>
                    <span className="truncate text-[13.5px] text-[#1E293B]">{shortName(log.user_name) || (log.user_id ? 'Compte' : 'Invité')}</span>
                    <LineIcon d={ICONS.chevron} size={14} color="#9CA3AF" width={2.2} />
                  </button>
                );
              })}
            </div>
          </div>
          {logs.length > 0 && (
            <div className="flex items-center justify-between gap-3 border-t border-[#EEF0F4] px-7 py-4">
              <span className="text-[13px] text-[#6B7280]">{logs.length.toLocaleString('fr-FR')} {logs.length > 1 ? 'dernières requêtes' : 'dernière requête'} sur {total.toLocaleString('fr-FR')}</span>
              {logs.length < total && <LinkButton disabled={loading} onClick={() => setOffset(logs.length)}>{loading ? 'Chargement…' : 'Charger la suite'}</LinkButton>}
            </div>
          )}
        </Card>
      </div>

      <aside className="hidden w-[380px] shrink-0 overflow-y-auto rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] xl:block">
        {selected ? (
          <LogDetail log={selected} onClose={() => setSelected(null)} onAuthFailures={onAuthFailures}
            onSameUser={selected.user_id ? () => { setUserId({ id: selected.user_id as string, name: shortName(selected.user_name) || 'Ce compte' }); setFiltersOpen(true); setChip('all'); reset(); } : undefined} />
        ) : (
          <div className="px-7 py-6">
            <SectionLabel>Requête</SectionLabel>
            <p className="mt-3 text-[13.5px] leading-[1.5] text-[#6B7280]">Choisissez une ligne pour voir ce qui a été envoyé et ce que l’API a répondu.</p>
            <div className="mt-6 h-2 -mx-7 bg-[#F1F2F8]" />
            <div className="pt-5"><SectionLabel>Actions</SectionLabel></div>
            <div className="-mx-7 mt-2">
              <ActionRow first icon={ICONS.doc} title="Voir les échecs de connexion" subtitle="Avec la cause et l’empreinte de l’APK" onClick={onAuthFailures} />
            </div>
          </div>
        )}
      </aside>
    </main>
  );
};

const LogDetail = ({ log, onClose, onAuthFailures, onSameUser }: { log: ApiLogEntry; onClose: () => void; onAuthFailures: () => void; onSameUser?: () => void }) => {
  const body = prettyJson(log.body);
  const response = prettyJson(log.response);
  return (
    <div>
      <div className="px-7 pb-5 pt-5">
        <div className="flex items-center justify-between">
          <SectionLabel>Requête</SectionLabel>
          <CloseButton onClick={onClose} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <MethodPill method={log.method} />
          <CodePill status={log.status} />
          <span className="text-[13px] text-[#6B7280]">{time(log.timestamp)} · {ms(log.execution_time_ms)}</span>
        </div>
        <p className="mt-2 break-all font-mono text-[15px] font-bold text-[#1E293B]">{log.route}</p>
      </div>

      {body && (
        <>
          <div className="h-2 bg-[#F1F2F8]" />
          <div className="px-7 py-5">
            <SectionLabel>Envoyé</SectionLabel>
            <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-2xl bg-[#F7F7FB] p-4 font-mono text-[12.5px] leading-[1.6] text-[#1E293B]">{body}</pre>
          </div>
        </>
      )}

      <div className="h-2 bg-[#F1F2F8]" />
      <div className="px-7 py-5">
        <SectionLabel>Réponse</SectionLabel>
        <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-2xl bg-[#0F172A] p-5 font-mono text-[12.5px] leading-[1.6] text-white">{response || 'Pas de corps'}</pre>
      </div>

      <div className="h-2 bg-[#F1F2F8]" />
      <div className="px-7 py-5">
        <SectionLabel>Détails</SectionLabel>
        <dl className="mt-3 space-y-2 text-[13px]">
          {[
            ['Personne', log.user_name || (log.user_id ? log.user_id : 'Invité')],
            ['Adresse IP', log.ip || '—'],
            ['Service', log.service || '—'],
            ['Client', log.user_agent || '—']
          ].map(([k, v]) => (
            <div key={k} className="flex gap-3">
              <dt className="w-24 shrink-0 text-[#6B7280]">{k}</dt>
              <dd className="min-w-0 break-all text-[#1E293B]">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="h-2 bg-[#F1F2F8]" />
      <div className="pb-3 pt-5">
        <div className="px-7"><SectionLabel>Actions</SectionLabel></div>
        <div className="mt-2">
          {onSameUser && <ActionRow first icon={ICONS.user} title="Ses autres requêtes" subtitle="Le parcours de ce compte" onClick={onSameUser} />}
          <ActionRow first={!onSameUser} icon={ICONS.doc} title="Voir les échecs de connexion" subtitle="Avec la cause et l’empreinte de l’APK" onClick={onAuthFailures} />
        </div>
      </div>
    </div>
  );
};

export default ManagementApiLogs;
